import { CollideEvent, CollideId, isOneHitCollider } from '../../defs/collide';
import type { ChocolateEggBlockEntity } from '../../entities/BlockEntity';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { BreakEffect } from '../effects/BreakEffect';
import { GoalEffect } from '../effects/GoalEffect';
import { InvalidEffect } from '../effects/InvalidEffect';
import { CollideHandler, ICollision } from './ICollision';

/*
    chocolate egg block collision resolver
*/
export class ChocolateEggCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: ChocolateEggBlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this._resolveTap,
    block: this._resolveBlock,
    attack: this._resolveAttack,
  };

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, entity: ChocolateEggBlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveTap(event: CollideEvent) {
    // invalid effect
    await new InvalidEffect(this._entity).execute();
    return false;
  }

  private async _resolveBlock(event: CollideEvent) {
    this._attack();
    return true;
  }

  private async _resolveAttack(event: CollideEvent) {
    if (isOneHitCollider(event.collider?.type)) {
      this._entity.c.blockChocolateEgg.count = 0;
    }
    this._attack();
    return true;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _attack() {
    const entity = this._entity;
    const position = entity.c.position.mapPosition;
    const component = entity.c.blockChocolateEgg;

    // execute break effect
    await new BreakEffect(this._scene, {
      position,
      size: entity.c.block.props,
      color: component.count < 2 ? 0xba5f3b : 0xeeeeee,
    }).execute();

    // if count is 0
    if (component.count <= 0) {
      const rotation = entity.rotation;

      // despawn block
      despawnBlockEntity(this._scene, this._entity);

      await new GoalEffect(this._scene, {
        position,
        id: 'chocolateEgg',
        scale: 1,
        rotation,
      }).execute();

      // else decrement count
    } else {
      --component.count;
      this._scene?.events.publish({ id: 'poke' });
    }
  }
}
